import { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { useTranslation } from 'react-i18next';
import {
  Activity,
  Cpu,
  HardDrive,
  Shield,
  Zap,
  Users,
  BarChart3,
  Clock,
  CheckCircle2,
  ArrowUpRight,
  ArrowRight,
  Lock,
  Eye,
  EyeOff,
  ChevronRight,
  Globe,
  Terminal,
  Bot,
  Database,
  Workflow,
  Bell,
  Settings,
} from "lucide-react";

const fakeAgents = [
  { name: "Ops-Alpha", status: "active", tasks: 12, cpu: "34%" },
  { name: "Support-Beta", status: "active", tasks: 8, cpu: "22%" },
  { name: "Analytics-Gamma", status: "idle", tasks: 0, cpu: "3%" },
  { name: "Ingest-Delta", status: "active", tasks: 24, cpu: "61%" },
];

const fakeEvents = [
  { time: "01:08", event: "Agent Ops-Alpha completed batch job #4821", type: "success" },
  { time: "01:05", event: "Storage autoscaled to 256GB", type: "info" },
  { time: "01:02", event: "New integration connected: Slack workspace", type: "success" },
  { time: "00:58", event: "Security scan completed — no issues found", type: "success" },
  { time: "00:55", event: "Agent Ingest-Delta started processing queue", type: "info" },
  { time: "00:51", event: "Uptime check passed — 99.97% (30d)", type: "success" },
];

const fakeIntegrations = [
  { name: "Slack", connected: true },
  { name: "GitHub", connected: true },
  { name: "Notion", connected: true },
  { name: "Stripe", connected: false },
  { name: "AWS", connected: true },
  { name: "Gmail", connected: false },
];

const plans = [
  { name: "Max OS Cloud", price: "$297", agents: "1 agent", storage: "16GB", support: "Standard", active: true },
  { name: "Max OS Cloud Enterprise", price: "$899", agents: "Multi-agent", storage: "60GB", support: "Premium", active: false },
  { name: "Custom", price: "Contact", agents: "Unlimited", storage: "Unlimited", support: "24/7 Dedicated", active: false },
];

export default function MaxOSCloudPreview({ embedded = false }) {
  const { t } = useTranslation();
  const [showData, setShowData] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <div className={embedded ? "bg-[#09090B] text-white" : "min-h-screen bg-[#09090B] text-white"}>
      {!embedded && (
        <SEOHead
          title={t('max_o_s_cloud_preview.max_os_cloud_ai_cloud_dashboard_preview_max')}
          description="Preview the Max OS Cloud dashboard. See how AI digital workers operate, monitor agents, track usage, and manage integrations in real-time."
          url="/preview/max-os-cloud"
          noIndex
        />
      )}
      {!embedded && <Header />}

      <div className="mt-12 border-t border-zinc-900 pt-12 pb-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
            // THIS IS A PREVIEW
          </div>
          <h2 className="font-display text-3xl md:text-4xl uppercase leading-[0.95] mb-4">
            This is the <span className="text-amber-500">{t('api_docs.automations.builder.preview')}</span> for inactive accounts.
          </h2>
          <p className="text-zinc-400 mb-8 leading-relaxed">
            Your live dashboard appears only after Max OS Cloud is activated. Until then, this page shows the product preview, sample metrics, and plan comparison.
          </p>
          <div className="flex justify-center gap-3 flex-wrap">
            <a
              href="/products/max-os-cloud"
              className="group inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 text-black px-10 py-5 font-bold uppercase tracking-wider text-sm transition-colors"
            >
              Deploy to Cloud
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="/products/max-os-cloud-enterprise"
              className="inline-flex items-center gap-3 border border-zinc-800 hover:border-zinc-500 text-white px-10 py-5 font-bold uppercase tracking-wider text-sm transition-colors"
            >
              View enterprise plan
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-5">
        <div className="border border-amber-500/20 bg-amber-500/5 px-5 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-1">
              // PREVIEW-ONLY ACCESS
            </div>
            <p className="text-sm text-zinc-300">
              This account does not have an active Max OS Cloud plan yet, so you’re seeing the product preview instead of the live dashboard.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="/products/max-os-cloud"
              className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2.5 font-bold uppercase tracking-wider text-[10px] transition-colors"
            >
              Activate plan
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-zinc-500 text-white px-4 py-2.5 font-bold uppercase tracking-wider text-[10px] transition-colors"
            >
              Compare plans
            </a>
          </div>
        </div>
      </div>

      {/* Prototype banner */}
      <div className="bg-amber-500/10 border-b border-amber-500/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">
              Preview Mode — Max OS Cloud Dashboard Concept
            </span>
          </div>
          <button
            onClick={() => setShowData(!showData)}
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-500 transition-colors"
          >
            {showData ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showData ? "Blur data" : "Reveal data"}
          </button>
        </div>
      </div>

      {/* Dashboard shell */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-1">
              // MAX OS CLOUD
            </div>
            <h1 className="font-display text-3xl uppercase">{t('max_o_s_cloud_preview.control_center')}</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 border border-zinc-800 bg-zinc-950">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{t('max_o_s_cloud_preview.all_systems_operational')}</span>
            </div>
            <button className="p-2 border border-zinc-800 hover:border-zinc-600 transition-colors">
              <Bell className="w-4 h-4 text-zinc-400" />
            </button>
            <button className="p-2 border border-zinc-800 hover:border-zinc-600 transition-colors">
              <Settings className="w-4 h-4 text-zinc-400" />
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-8 border-b border-zinc-900 pb-px">
          {["overview", "agents", "usage", "integrations"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors border-b-2 ${
                activeTab === tab
                  ? "border-amber-500 text-amber-500"
                  : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {activeTab === "overview" && (
          <div className="space-y-8">
            {/* Metric tiles */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
              {[
                { icon: Bot, label: "Active Agents", value: showData ? "4" : "—", sub: "of 5 licensed", color: "text-amber-500" },
                { icon: Cpu, label: "Compute Usage", value: showData ? "47%" : "—", sub: "of 16 vCPUs", color: "text-sky-400" },
                { icon: HardDrive, label: "Storage", value: showData ? "128 GB" : "—", sub: "of 256 GB", color: "text-green-400" },
                { icon: Activity, label: "Tasks Today", value: showData ? "1,247" : "—", sub: "+12% vs yesterday", color: "text-purple-400" },
              ].map((m, i) => (
                <div key={i} className="bg-[#09090B] p-6">
                  <div className="flex items-center justify-between mb-4">
                    <m.icon className={`w-5 h-5 ${m.color}`} />
                    <ArrowUpRight className="w-3 h-3 text-zinc-700" />
                  </div>
                  <div className={`font-display text-2xl ${showData ? "blur-none" : "blur-sm select-none"} transition-all`}>
                    {m.value}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                    {m.label}
                  </div>
                  <div className="font-mono text-[9px] text-zinc-600 mt-0.5">{m.sub}</div>
                </div>
              ))}
            </div>

            {/* Agents + Events two-col */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {/* Agents */}
              <div className="lg:col-span-2 border border-zinc-900 bg-zinc-950">
                <div className="p-4 border-b border-zinc-900 flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
                    // AGENTS
                  </div>
                  <Users className="w-3.5 h-3.5 text-zinc-600" />
                </div>
                <div className="divide-y divide-zinc-900">
                  {fakeAgents.map((a, i) => (
                    <div key={i} className="p-4 flex items-center justify-between hover:bg-zinc-900/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${a.status === "active" ? "bg-green-500" : "bg-zinc-600"}`} />
                        <div>
                          <div className={`text-sm font-mono ${showData ? "" : "blur-sm select-none"} transition-all`}>
                            {a.name}
                          </div>
                          <div className="font-mono text-[9px] text-zinc-600">
                            {a.tasks} tasks · CPU {a.cpu}
                          </div>
                        </div>
                      </div>
                      <span className={`font-mono text-[9px] uppercase tracking-wider px-2 py-1 ${
                        a.status === "active"
                          ? "bg-green-500/10 text-green-400 border border-green-500/20"
                          : "bg-zinc-800 text-zinc-500 border border-zinc-700"
                      }`}>
                        {a.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent events */}
              <div className="lg:col-span-3 border border-zinc-900 bg-zinc-950">
                <div className="p-4 border-b border-zinc-900 flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
                    // RECENT EVENTS
                  </div>
                  <Clock className="w-3.5 h-3.5 text-zinc-600" />
                </div>
                <div className="divide-y divide-zinc-900">
                  {fakeEvents.map((e, i) => (
                    <div key={i} className="p-4 flex items-start gap-4 hover:bg-zinc-900/50 transition-colors">
                      <div className="font-mono text-[10px] text-zinc-600 shrink-0 mt-0.5 w-10">
                        {showData ? e.time : "--:--"}
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${
                          e.type === "success" ? "text-green-500" : "text-sky-400"
                        }`} />
                        <span className={`text-sm text-zinc-300 ${showData ? "" : "blur-sm select-none"} transition-all`}>
                          {e.event}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Integrations strip */}
            <div className="border border-zinc-900 bg-zinc-950 p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-4">
                // INTEGRATIONS
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {fakeIntegrations.map((intg, i) => (
                  <div
                    key={i}
                    className={`border p-4 flex items-center gap-3 transition-colors ${
                      intg.connected
                        ? "border-zinc-800 bg-zinc-900/50 hover:border-zinc-700"
                        : "border-zinc-900 bg-zinc-950 opacity-50"
                    }`}
                  >
                    <Globe className={`w-4 h-4 ${intg.connected ? "text-amber-500" : "text-zinc-700"}`} />
                    <div>
                      <div className={`text-xs font-mono ${showData ? "" : "blur-sm select-none"} transition-all`}>
                        {intg.name}
                      </div>
                      <div className={`font-mono text-[8px] uppercase tracking-wider ${
                        intg.connected ? "text-green-500" : "text-zinc-600"
                      }`}>
                        {intg.connected ? "Connected" : "Available"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Plan + upgrade CTA */}
            <div className="border border-amber-500/30 bg-amber-500/5 p-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                <div className="lg:col-span-2">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">
                    // YOUR PLAN
                  </div>
                  <h3 className="font-display text-2xl uppercase mb-2">Growth — $399/mo</h3>
                  <p className="text-zinc-400 text-sm">
                    5 agents · 256GB storage · Priority support · All integrations
                  </p>
                </div>
                <div className="flex flex-col gap-3">
                  <button className="group inline-flex items-center justify-center gap-3 bg-amber-500 hover:bg-amber-400 text-black px-6 py-4 font-bold uppercase tracking-wider text-xs transition-colors">
                    Upgrade to Enterprise
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </button>
                  <div className="flex items-center justify-center gap-2 text-zinc-500">
                    <Lock className="w-3 h-3" />
                    <span className="font-mono text-[9px] uppercase tracking-wider">{t('max_o_s_cloud_preview.need_more_unlock_unlimited_agents')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Agents tab */}
        {activeTab === "agents" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
              {fakeAgents.map((a, i) => (
                <div key={i} className="bg-[#09090B] p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Bot className="w-5 h-5 text-amber-500" />
                      <div>
                        <div className={`font-display text-lg uppercase ${showData ? "" : "blur-sm select-none"} transition-all`}>
                          {a.name}
                        </div>
                        <div className="font-mono text-[9px] text-zinc-600 uppercase tracking-wider">
                          {a.status}
                        </div>
                      </div>
                    </div>
                    <span className={`font-mono text-[9px] uppercase tracking-wider px-2 py-1 ${
                      a.status === "active"
                        ? "bg-green-500/10 text-green-400 border border-green-500/20"
                        : "bg-zinc-800 text-zinc-500 border border-zinc-700"
                    }`}>
                      {a.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-1">{t('dashboard.tasks')}</div>
                      <div className={`font-mono text-lg ${showData ? "" : "blur-sm select-none"} transition-all`}>
                        {a.tasks}
                      </div>
                    </div>
                    <div>
                      <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-1">{t('dashboard.cpu')}</div>
                      <div className={`font-mono text-lg ${showData ? "" : "blur-sm select-none"} transition-all`}>
                        {a.cpu}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border border-dashed border-zinc-800 p-8 text-center">
              <Bot className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                Agent slots: 4 / 5 used
              </div>
              <p className="text-zinc-600 text-sm mb-4">{t('max_o_s_cloud_preview.upgrade_to_enterprise_for_unlimited_agents')}</p>
              <button className="inline-flex items-center gap-2 border border-amber-500/30 text-amber-500 hover:bg-amber-500/10 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors">
                Add agent
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        {/* Usage tab */}
        {activeTab === "usage" && (
          <div className="space-y-6">
            {[
              { label: "Compute (vCPUs)", used: 7.5, total: 16, unit: "vCPUs", pct: 47 },
              { label: "Memory", used: 12.4, total: 32, unit: "GB", pct: 39 },
              { label: "Storage", used: 128, total: 256, unit: "GB", pct: 50 },
              { label: "API Calls (30d)", used: 84200, total: 250000, unit: "calls", pct: 34 },
            ].map((u, i) => (
              <div key={i} className="border border-zinc-900 bg-zinc-950 p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
                    {u.label}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500">
                    {showData ? `${u.used} / ${u.total} ${u.unit}` : "—"}
                  </div>
                </div>
                <div className="w-full h-2 bg-zinc-900 overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      u.pct > 80 ? "bg-red-500" : u.pct > 60 ? "bg-amber-500" : "bg-green-500"
                    }`}
                    style={{ width: showData ? `${u.pct}%` : "0%" }}
                  />
                </div>
                <div className="font-mono text-[9px] text-zinc-600 mt-2">
                  {showData ? `${u.pct}% utilized` : "—"}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Integrations tab */}
        {activeTab === "integrations" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
              {fakeIntegrations.map((intg, i) => (
                <div key={i} className="bg-[#09090B] p-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe className={`w-5 h-5 ${intg.connected ? "text-amber-500" : "text-zinc-700"}`} />
                    <div>
                      <div className={`font-display text-sm uppercase ${showData ? "" : "blur-sm select-none"} transition-all`}>
                        {intg.name}
                      </div>
                      <div className={`font-mono text-[9px] uppercase tracking-wider ${
                        intg.connected ? "text-green-500" : "text-zinc-600"
                      }`}>
                        {intg.connected ? "Connected" : "Not connected"}
                      </div>
                    </div>
                  </div>
                  <button className={`font-mono text-[9px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                    intg.connected
                      ? "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                      : "border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                  }`}>
                    {intg.connected ? "Manage" : "Connect"}
                  </button>
                </div>
              ))}
            </div>
            <div className="border border-dashed border-zinc-800 p-8 text-center">
              <Workflow className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                Custom integrations available
              </div>
              <p className="text-zinc-600 text-sm">{t('max_o_s_cloud_preview.enterprise_plan_includes_custom_api_connectors')}</p>
            </div>
          </div>
        )}

      </div>

      {!embedded && <Footer />}
    </div>
  );
}
